import { inchesToFeet, inchesToSquareFeet } from "legacy/helpers.es6"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

class AvailabilityCalendar extends React.Component {
  constructor(props) {
    super(props)

    // Bind methods
    this.shouldRenderExtraRow = this.shouldRenderExtraRow.bind(this)
    this.isTypeLOA = this.isTypeLOA.bind(this)
    this.isTypeSquareFoot = this.isTypeSquareFoot.bind(this)
    this.datesForQuery = this.datesForQuery.bind(this)
    this.isDateForQuery = this.isDateForQuery.bind(this)
    this.dataForDate = this.dataForDate.bind(this)
    this.convertedOccupancyForDate = this.convertedOccupancyForDate.bind(this)
    this.convertedTotal = this.convertedTotal.bind(this)
    this.convertedAvailabilityForDate =
      this.convertedAvailabilityForDate.bind(this)
    this.displayUnit = this.displayUnit.bind(this)
    this.calcColor = this.calcColor.bind(this)
    this.popoverData = this.popoverData.bind(this)
    this.dateData = this.dateData.bind(this)
    this.renderDate = this.renderDate.bind(this)
  }

  shouldRenderExtraRow(firstDayOfWeek, daysInMonth) {
    const minimumDateIndex = 36

    return firstDayOfWeek + daysInMonth < minimumDateIndex
  }

  isTypeLOA() {
    return ["SpaceGroup::Linear", "SpaceGroup::LinearV2"].includes(
      this.props.type
    )
  }

  isTypeSquareFoot() {
    return this.props.type === "SpaceGroup::SquareFootage"
  }

  datesForQuery() {
    return this.props.nights.map((night) => night.night)
  }

  isDateForQuery(date) {
    return this.datesForQuery().includes(date.format("YYYY-MM-DD"))
  }

  dataForDate(date) {
    return this.props.nights.find(
      (night) => night.night === date.format("YYYY-MM-DD")
    )
  }

  convertedOccupancyForDate(date) {
    const nightData = this.dataForDate(date)
    if (!nightData) return 0

    if (this.isTypeLOA()) {
      return nightData.loa ? inchesToFeet(nightData.loa) : 0
    } else if (this.isTypeSquareFoot()) {
      return nightData.square_inches
        ? inchesToSquareFeet(nightData.square_inches)
        : 0
    } else {
      return nightData.count || 0
    }
  }

  convertedTotal() {
    if (this.isTypeLOA()) {
      return inchesToFeet(this.props.total)
    } else if (this.isTypeSquareFoot()) {
      return inchesToSquareFeet(this.props.total)
    } else {
      return this.props.total
    }
  }

  convertedAvailabilityForDate(date) {
    return this.convertedTotal() - this.convertedOccupancyForDate(date)
  }

  displayUnit() {
    if (this.isTypeLOA()) {
      return "'"
    } else if (this.isTypeSquareFoot()) {
      return "sqft"
    } else {
      return ""
    }
  }

  calcColor(date) {
    if (this.isDateForQuery(date)) {
      const occupancy = this.convertedOccupancyForDate(date)
      if (occupancy >= this.convertedTotal()) {
        return "bg-rich-red cursor-pointer"
      } else if (
        occupancy >= this.convertedTotal() * 0.9 ||
        this.convertedTotal() - occupancy === 1
      ) {
        return "bg-bright-yellow cursor-pointer"
      } else {
        return "bg-soft-green cursor-pointer"
      }
    } else {
      return "bg-border-gray"
    }
  }

  popoverData(date) {
    if (this.isDateForQuery(date)) {
      return `
        <div class="font-semibold">
          <span class="text-muted">DATE</span>
          <span class="bs-ml-2">${date.format("L")}</span>
        </div>
        <div class="font-semibold">
          <span class="text-muted">AVAILABLE</span>
          <span class="bs-ml-2">${`${this.convertedAvailabilityForDate(
            date
          )}${this.displayUnit()}`}</span>
        </div>
      `
    }
  }

  dateData(date) {
    if (this.isDateForQuery(date)) {
      const available = this.convertedAvailabilityForDate(date)

      if (available > 99) {
        if (this.props.isShortTimeFrame) {
          return `99${this.displayUnit()}+`
        } else {
          return "+"
        }
      } else {
        return `${available}${this.displayUnit()}`
      }
    }
  }

  render() {
    if (!this.props.nights) return null

    let date = moment(this.props.nights[0].night, "YYYY-MM-DD")
    if (this.props.isShortTimeFrame) {
      const daysInTimeFrame = this.props.nights.length
      return (
        <div className="bs-px-3 flex items-center space-x-2 overflow-x-auto">
          {Array.from({ length: daysInTimeFrame }).map((_, i) => {
            const html = this.renderDate(date)
            date = date.clone().add(1, "days")
            return html
          })}
        </div>
      )
    } else {
      date = date.startOf("month")
      const daysInMonth = date.daysInMonth()
      const firstDayOfWeek = Number(date.format("d"))
      return (
        <div
          className="bg-lightish-gray col-xs-3 bs-py-3 bs-mr-4 bs-my-3"
          style={{ width: "190" }}
        >
          <p className="text-center font-semibold">
            {date.format("MMMM YYYY")}
          </p>
          <div className="grid grid-cols-7 justify-items-center gap-y-2">
            <div className="font-size-10">S</div>
            <div className="font-size-10">M</div>
            <div className="font-size-10">T</div>
            <div className="font-size-10">W</div>
            <div className="font-size-10">T</div>
            <div className="font-size-10">F</div>
            <div className="font-size-10">S</div>
            {Array.from({ length: daysInMonth }).map(() => {
              const html = this.renderDate(date)
              date = date.clone().add(1, "days")
              return html
            })}
            {this.shouldRenderExtraRow(firstDayOfWeek, daysInMonth) && (
              <div className="col-start-1" style={{ height: "20px" }} />
            )}
          </div>
        </div>
      )
    }
  }

  renderDate(date) {
    return (
      <div
        key={`${date.format("YYYY-MM-DD")}-availability-date`}
        className={`col-start-${Number(date.format("d")) + 1} ${this.calcColor(
          date
        )} flex items-center justify-center rounded px-1 font-semibold text-white hover:scale-150 hover:shadow-md`}
        style={{
          width: this.props.isShortTimeFrame ? 35 : 20,
          height: 20,
          fontSize: 12,
        }}
        data-content={this.popoverData(date)}
        data-container="body"
        data-toggle="popover"
        data-placement="top"
        data-animation="false"
        data-html="true"
      >
        <span>{this.dateData(date)}</span>
      </div>
    )
  }
}

AvailabilityCalendar.propTypes = {
  nights: PropTypes.arrayOf(
    PropTypes.shape({
      night: PropTypes.string.isRequired,
      count: PropTypes.number,
      loa: PropTypes.number,
      square_inches: PropTypes.number,
    })
  ),
  total: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  isShortTimeFrame: PropTypes.bool,
}

export default AvailabilityCalendar
